import { logger } from './logger';
/**
 * Token Expiration Utility
 *
 * Provides utilities for detecting and handling JWT token expiration.
 * Used for proactive token refresh to prevent user session interruptions.
 */

/**
 * Configuration constants for token expiration handling
 */
export const TOKEN_EXPIRATION_BUFFER_SECONDS = 300; // 5 minutes
export const TOKEN_CHECK_INTERVAL_MS = 60000; // 1 minute
export const REFRESH_ATTEMPT_COOLDOWN_MS = 300000; // 5 minutes

/**
 * JWT payload interface with standard claims
 */
export interface JwtPayload {
  /** Expiration time (seconds since Unix epoch) */
  exp?: number;
  /** Issued at time (seconds since Unix epoch) */
  iat?: number;
  /** Issuer */
  iss?: string;
  /** Subject (user ID) */
  sub?: string;
  /** Audience */
  aud?: string | string[];
  /** Object ID (Entra user ID) */
  oid?: string;
  /** Email */
  email?: string;
  /** Tenant ID */
  tid?: string;
  /** Other custom claims */
  [key: string]: unknown;
}

/**
 * Decode a JWT token without verification (client-side only)
 *
 * ⚠️  WARNING — Microsoft best practice:
 * Access tokens issued by Entra ID / Microsoft identity platform MUST be treated
 * as opaque strings by SPA clients. Do NOT call this function on access tokens.
 * Only the resource API (backend) should inspect or validate access tokens.
 *
 * This function is safe to use on:
 * - ID tokens (for display-only purposes, not for authorization)
 * - Locally-issued tokens (e.g. test JWTs)
 *
 * For checking session validity in an SPA, use acquireTokenSilent and catch
 * InteractionRequiredAuthError, or read `account.idTokenClaims` from MSAL.
 *
 * @see https://learn.microsoft.com/en-us/entra/identity-platform/access-tokens#validate-tokens
 *
 * @param token - JWT token string (ID token or locally-issued token only)
 * @returns Decoded payload or null if invalid
 */
export function decodeJwt(token: string): JwtPayload | null {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      logger.warn('[tokenExpiration] Invalid JWT format - expected 3 parts');
      return null;
    }

    // Decode base64url payload (second part)
    const payload = parts[1];
    if (!payload) {
      logger.warn('[tokenExpiration] Invalid JWT format - missing payload');
      return null;
    }

    // Replace base64url chars with base64 chars
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

    // Decode base64 to JSON string
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload) as JwtPayload;
  } catch (error) {
    logger.error('[tokenExpiration] Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Check if a token has expired based on its exp claim
 *
 * @param token - JWT token string
 * @returns True if token is expired, false otherwise
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJwt(token);
  if (!payload || typeof payload.exp !== 'number') {
    logger.warn('[tokenExpiration] Token missing exp claim');
    return true; // Treat as expired if no exp claim
  }

  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();

  return currentTime >= expirationTime;
}

/**
 * Check if a token will expire soon (within buffer period)
 *
 * @param token - JWT token string
 * @param bufferSeconds - Time buffer in seconds (default: 5 minutes)
 * @returns True if token expires within buffer period, false otherwise
 */
export function isTokenExpiringSoon(
  token: string,
  bufferSeconds: number = TOKEN_EXPIRATION_BUFFER_SECONDS
): boolean {
  const payload = decodeJwt(token);
  if (!payload || typeof payload.exp !== 'number') {
    logger.warn('[tokenExpiration] Token missing exp claim');
    return true; // Treat as expiring if no exp claim
  }

  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  const bufferMs = bufferSeconds * 1000;

  // Check if current time + buffer >= expiration time
  return currentTime + bufferMs >= expirationTime;
}

/**
 * Get the expiration time of a token as a Date object
 *
 * @param token - JWT token string
 * @returns Expiration date or null if invalid
 */
export function getTokenExpirationTime(token: string): Date | null {
  const payload = decodeJwt(token);
  if (!payload || typeof payload.exp !== 'number') {
    return null;
  }

  // exp is in seconds, convert to milliseconds for Date
  return new Date(payload.exp * 1000);
}

/**
 * Get the remaining lifetime of a token in seconds
 *
 * @param token - JWT token string
 * @returns Remaining seconds until expiration, or 0 if expired/invalid
 */
export function getRemainingTokenLifetime(token: string): number {
  const payload = decodeJwt(token);
  if (!payload || typeof payload.exp !== 'number') {
    return 0;
  }

  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  const remainingMs = expirationTime - currentTime;

  // Return seconds (rounded down), minimum 0
  return Math.max(0, Math.floor(remainingMs / 1000));
}

/**
 * Get the session expiry timestamp from an MSAL AccountInfo object.
 *
 * Uses `idTokenClaims.exp` which is co-issued with the access token under
 * Entra External ID and shares the same expiry window. Reading from the account
 * object avoids decoding the access token (which is opaque for SPA clients).
 *
 * Returns the expiry as a Unix epoch in milliseconds, or null if unavailable.
 *
 * @param idTokenClaims - The idTokenClaims record from an MSAL AccountInfo
 */
export function getExpiryFromIdTokenClaims(
  idTokenClaims: Record<string, unknown> | undefined | null
): number | null {
  if (!idTokenClaims || typeof idTokenClaims['exp'] !== 'number') {
    return null;
  }
  // idTokenClaims.exp is in seconds (standard JWT); convert to milliseconds
  return (idTokenClaims['exp'] as number) * 1000;
}

/**
 * Check if the session represented by MSAL idTokenClaims has expired.
 *
 * Prefer this over decoding the access token — ID token claims are the
 * safe, supported way to read expiry on the client.
 *
 * @param idTokenClaims - The idTokenClaims record from an MSAL AccountInfo
 */
export function isSessionExpiredFromClaims(
  idTokenClaims: Record<string, unknown> | undefined | null
): boolean {
  const expiresAtMs = getExpiryFromIdTokenClaims(idTokenClaims);
  if (expiresAtMs === null) return true; // treat unknown as expired
  return Date.now() >= expiresAtMs;
}

/**
 * Format remaining token lifetime as human-readable string
 *
 * @param token - JWT token string
 * @returns Formatted string like "5 minutes" or "expired"
 */
export function formatTokenLifetime(token: string): string {
  const remainingSeconds = getRemainingTokenLifetime(token);

  if (remainingSeconds === 0) {
    return 'expired';
  }

  const minutes = Math.floor(remainingSeconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  } else {
    return `${remainingSeconds} second${remainingSeconds > 1 ? 's' : ''}`;
  }
}
